/**
 * `charts/labels.ts` — the label rules every bar chart on the portal follows (launch round,
 * 7 Oct 2026: "some of the labels are overflowing on bars and not visually pleasing; we need
 * to show high-level professional development and design").
 *
 * Four rules, one place:
 *
 * 1. **Row names are never cut.** The label column is as wide as the longest name needs,
 *    never narrower than the chart's own minimum and never wider than 45 % of the chart; a
 *    name longer than that wraps onto a second line and its row grows to hold it.
 * 2. **A value sits outside its bar**, `VALUE_GAP` past the end, on a halo of the card's
 *    own paper (`VALUE_HALO`) so a reference line behind it stays readable. Never inside.
 * 3. **The axis has room for the longest value**, measured: the maximum is the smallest
 *    clean number at which every bar's printed figure still ends inside the plot, so a
 *    figure never leaves the frame and a bar is never shorter than its label needs.
 * 4. **A figure never sits on a reference line**: where the line would cross it, the
 *    figure moves past the line.
 */
import type { CSSProperties } from 'react';

import { fmtNum } from '../../lib/format';
import { chartFont, textWidth, wrapText } from '../../lib/text';
import { niceTicks } from './scale';

/** The widest a row-label column may be, as a share of the chart's width. */
export const LABEL_MAX_SHARE = 0.45;
/** Line height of a wrapped label, as a multiple of its font size. */
export const LABEL_LINE_H = 1.15;
/** Space between a label's right edge and the plot. */
export const LABEL_PAD = 6;
/** Space between a bar's end and its printed value. */
export const VALUE_GAP = 6;

/**
 * The halo under a printed value: the stroke is painted first, in the panel's own white,
 * and the glyphs on top of it. Inline rather than a class so it travels with an exported SVG.
 */
export const VALUE_HALO: CSSProperties = {
  paintOrder: 'stroke',
  stroke: 'var(--panel, #FFFFFF)',
  strokeWidth: 3,
  strokeLinejoin: 'round',
};

export interface LabelColumn {
  /** The column's width, the label's right edge sitting `LABEL_PAD` inside it. */
  width: number;
  /** Each label as the lines it is drawn on (one, or two when it is longer than the cap). */
  lines: string[][];
}

/**
 * The label column for a set of row names: as wide as the longest needs, at least `min`,
 * at most `LABEL_MAX_SHARE` of `total`. Names wider than that wrap.
 */
export function labelColumn(
  labels: readonly string[],
  { size, min, total, weight = 400 }: { size: number; min: number; total: number; weight?: number },
): LabelColumn {
  const longest = labels.reduce((m, l) => Math.max(m, textWidth(l, size, weight)), 0);
  const cap = Math.max(min, total * LABEL_MAX_SHARE);
  const width = Math.min(cap, Math.max(min, Math.ceil(longest + LABEL_PAD + 2)));
  const room = width - LABEL_PAD - 2;
  const font = chartFont(size, weight);
  return { width, lines: labels.map((l) => wrapText(l, room, font, 2)) };
}

/** Height a label of `lines` lines takes at `size`. */
export function labelHeight(lines: number, size: number): number {
  return Math.max(1, lines) * size * LABEL_LINE_H;
}

/** The baseline of line `i` of `n` of a label block centred on `cy`. */
export function lineY(cy: number, size: number, i: number, n: number): number {
  return cy + size * 0.35 + (i - (n - 1) / 2) * size * LABEL_LINE_H;
}

/**
 * The printed figure for a value: `fmtNum` alone, because the unit is said once in the axis
 * title, except `%`, which a reader expects beside every share.
 */
export function valueLabel(value: number, fmt: number, unit?: string): string {
  return `${fmtNum(value, fmt)}${unit === '%' ? '%' : ''}`;
}

/**
 * Where a value's text starts: `VALUE_GAP` past the bar's end, or past a reference line
 * that would otherwise run through it.
 */
export function placeValue(end: number, width: number, refXs: readonly number[] = []): number {
  let x = end + VALUE_GAP;
  for (const refX of [...refXs].sort((a, b) => a - b)) {
    if (refX >= x - 2 && refX <= x + width + 2) x = refX + VALUE_GAP;
  }
  return x;
}

/**
 * A round number at or just above `v`: two significant figures on a 5-step, so
 * 1,163 → 1,200 and 1,020,000 → 1,050,000. Used for the axis end, which need not be a tick.
 */
export function niceCeil(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 1;
  const unit = 5 * 10 ** (Math.floor(Math.log10(v)) - 2);
  return Number((Math.ceil(v / unit - 1e-9) * unit).toPrecision(12));
}

export interface ValueAxisItem {
  /** The bar's value; `null` draws no bar but may still print an em dash at zero. */
  v: number | null;
  /** The width of its printed figure. */
  w: number;
}

/**
 * The value axis of a bar chart whose figures print past the bar ends.
 *
 * `max` is `niceCeil` of the smallest maximum at which every figure (bar end + gap + its
 * own width, moved past any reference line it would cross) ends inside `plotW`; `ticks`
 * are the clean ticks of that range that fall inside it. `floor` keeps a scale a reader
 * expects (a share's 100) from shrinking below it.
 */
export function valueAxis({
  items,
  plotW,
  refs = [],
  floor = 0,
  steps = 4,
}: {
  items: readonly ValueAxisItem[];
  plotW: number;
  refs?: readonly number[];
  floor?: number;
  steps?: number;
}): { max: number; ticks: number[] } {
  const finiteRefs = refs.filter((r) => Number.isFinite(r) && r > 0);
  const peak = Math.max(
    0,
    floor,
    ...finiteRefs,
    ...items.map((it) => (it.v !== null && Number.isFinite(it.v) ? it.v : 0)),
  );
  if (peak <= 0) return { max: 1, ticks: niceTicks(1, steps) };
  // The maximum at which a figure starting at `start` (in value units) still fits.
  const need = (start: number, w: number) => {
    const room = plotW - VALUE_GAP - w;
    return room > plotW * 0.15 ? (start * plotW) / room : start / 0.15;
  };
  let required = peak;
  for (const it of items) {
    if (it.v === null || !Number.isFinite(it.v) || it.v <= 0) continue;
    required = Math.max(required, need(it.v, it.w));
  }
  let max = niceCeil(required);
  // A figure moved past a reference line needs room from the line, not from its bar.
  for (let pass = 0; pass < 3; pass++) {
    let grow = max;
    for (const it of items) {
      if (it.v === null || !Number.isFinite(it.v) || it.v <= 0) continue;
      const end = (it.v / max) * plotW;
      const x = placeValue(end, it.w, finiteRefs.map((r) => (r / max) * plotW));
      if (x + it.w > plotW + 0.5) grow = Math.max(grow, niceCeil(need(((x - VALUE_GAP) / plotW) * max, it.w)));
    }
    if (grow <= max) break;
    max = grow;
  }
  return axisTicks(peak, max, steps);
}

/**
 * Ticks for an axis running 0 → `max` over bars that peak at `peak`: the bars' own clean
 * step (`niceTicks(peak)`), carried on as far as `max` reaches. An end within 8 % of the
 * next tick takes that tick, so the axis closes on a number where it nearly does anyway.
 */
export function axisTicks(peak: number, maxIn: number, steps = 4): { max: number; ticks: number[] } {
  let max = maxIn;
  const base = niceTicks(peak > 0 ? peak : max, steps);
  const step = base[1]! - base[0]!;
  const next = Math.ceil(max / step - 1e-9) * step;
  if (next <= max * 1.08) max = Number(next.toPrecision(12));
  const ticks: number[] = [];
  for (let i = 0; i * step <= max * (1 + 1e-9); i++) ticks.push(Number((i * step).toPrecision(12)));
  return { max, ticks };
}
